import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { PaymentCheckout } from "../services/api";
import { loadScript } from "../utils/loadScript";
import { FaArrowLeft, FaShoppingBag, FaTrash, FaSpinner, FaPlus } from "react-icons/fa";
import ServiceSelectorModal from "../components/ServiceSelectorModal";
import { ShoppingCart } from "lucide-react";
import logo from "../assets/logo2.png";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

function SiteNavbar({ cartCount }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50 w-full">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 shrink-0">
            <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
          </div>
          <div className="hidden sm:block">
            <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
            <p className="text-gray-400 text-[10px]">University College Hospital</p>
          </div>
        </a>
        <div className="flex items-center gap-3">
          <a href="/track" className="hidden sm:block text-sm text-gray-500 hover:text-[#0d3d2e] font-medium transition">Track Transaction</a>
          <button onClick={() => navigate("/receipt")} className="relative p-2 text-gray-500 hover:text-[#0d3d2e] transition">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>
            )}
          </button>
          <button onClick={() => navigate(user ? "/dashboard" : "/login")}
            className="text-sm font-semibold text-white bg-[#0d3d2e] hover:bg-[#0a3226] px-4 py-2 rounded-lg transition">
            {user ? "Dashboard" : "Sign in"}
          </button>
        </div>
      </div>
    </nav>
  );
}

export default function ReceiptPage() {
  const { cart, updateCartItem, removeCartItem, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorFields, setErrorFields] = useState(new Set());
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMethods] = useState([
    { key: "monnify", name: "Card / Transfer" },
    { key: "remita", name: "Remita" },
  ]);
  const [selectedMethod, setSelectedMethod] = useState("monnify");
  const isLoadingMethods = false;

  const [payFor, setPayFor] = useState("self");

  const [customerDetails, setCustomerDetails] = useState({
    // PATIENT DETAILS
    patient_first_name: user?.first_name || "",
    patient_last_name: user?.last_name || "",
    patient_hospital_number: user?.kyc?.hospital_number || "",
    patient_email: user?.email || "",
    patient_phone_number: user?.phone_number || "",
    // PAYER DETAILS
    payer_first_name: user?.first_name || "",
    payer_last_name: user?.last_name || "",
    payer_email: user?.email || "",
    payer_phone_number: user?.phone_number || "",
    transaction_description: "",
  });

  const [checkoutResponse, setCheckoutResponse] = useState(null);
  const [scriptError, setScriptError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);

  const CHARGE_FEE = 100;

  const totalAmount = cart.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1),
    0
  );

  const paymentImages = {
    monnify: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQk4cKO96nyvsKiIGEnENpe37jAz5lTgBr5-g&s",
    remita: "https://blog.remita.net/wp-content/uploads/2024/04/ROUNDLOGO-1920x1024.png",
  };

  const paymentGateways = {
    remita: async (response) => {
      try {
        // Load Remita SDK if not yet available
        if (!window.RmPaymentEngine || typeof window.RmPaymentEngine.init !== "function") {
          await loadScript("https://login.remita.net/payment/v1/remita-pay-inline.bundle.js");
          // Give SDK a moment to initialise
          await new Promise(r => setTimeout(r, 500));
        }
        if (!window.RmPaymentEngine || typeof window.RmPaymentEngine.init !== "function") {
          throw new Error("Remita SDK failed to load. Check your internet connection and try again.");
        }

        const paymentEngine = window.RmPaymentEngine.init({
          key: "QVZQTHw2MzU4MjA1MDYzfDFkN2UxYTgxNmUwMzQzMzhkMWM2ZmYzMTRiYzgxYmFlNDNkNzVlZTliNDI0ZWZhODQ0YWU5MWJkY2I1OTYwZTMyYWFkYzljNWMwZDIxMjcxOTA1NDI0YjA5OWI1MTlkNTM4MTk0MGI0YjMzMmE2M2E3NmFjY2ZhNzQyYjE0Y2Y1",
          processRrr: true,
          transactionId: response.reference || `trx_${Date.now()}`,
          customerId: customerDetails.payer_email,
          firstName: `${customerDetails.payer_first_name}`,
          lastName: `${customerDetails.payer_last_name}`,
          email: customerDetails.payer_email,
          amount: totalAmount + CHARGE_FEE,
          narration: customerDetails.transaction_description || "Payment for cart items plus charge fee",
          extendedData: {
            customFields: [
              {
                name: "rrr",
                value: response.rrr,
              },
              {
                name: "hospital_number",
                value: customerDetails.patient_hospital_number,
              },
              {
                name: "patient_name",
                value: `${customerDetails.patient_first_name} ${customerDetails.patient_last_name}`,
              },
            ],
          },
          onSuccess: async (data) => {
            try {
              await axios.get(`https://api.uchselfservice.net/api/express/callback/remita/?transaction_id=${data.transactionId}&rrr=${response.rrr}&amount=${totalAmount + CHARGE_FEE}&charges=${CHARGE_FEE}`);
              setPaymentStatus("success");
              clearCart();
              navigate("/payment-success", { 
                state: { 
                  paymentData: response, 
                  customerDetails, 
                  cart, 
                  chargeFee: CHARGE_FEE 
                } 
              });
            } catch {
              setPaymentStatus("failed");
            }
          },
          onError: () => {
            setPaymentStatus("failed");
            setIsProcessing(false);
          },
          onClose: () => {
            // User closed widget — allow retry without re-creating invoice
            setPaymentStatus("cancelled");
            setIsProcessing(false);
          },
        });
        paymentEngine.showPaymentWidget();
      } catch (error) {
        setScriptError("Failed to initialize Remita payment. Please try another payment method.");
        setPaymentStatus("failed");
      }
    },
    monnify: async (response) => {
      try {
        if (!window.MonnifySDK) {
          await loadScript("https://sdk.monnify.com/plugin/monnify.js");
          await new Promise(r => setTimeout(r, 500));
        }
        // Append timestamp so each attempt gets a unique Monnify paymentReference.
        // Monnify treats paymentReference as a one-time token — reusing it (even across
        // retries) causes "Duplicate payment reference". The invoice DB reference
        // (response.reference) is sent separately as transaction_id for DB lookup.
        const invoiceRef = response.reference;
        const monnifyRef = `${invoiceRef}-${Date.now()}`;
        window.MonnifySDK.initialize({
          amount: totalAmount + CHARGE_FEE,
          currency: "NGN",
          reference: monnifyRef,
          customerFullName: `${customerDetails.payer_first_name} ${customerDetails.payer_last_name}`.trim(),
          customerEmail: customerDetails.payer_email,
          apiKey: "MK_PROD_4XA24J4TGS",
          contractCode: "765974016458",
          paymentDescription: customerDetails.transaction_description || "UCH Hospital Payment",
          paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
          metadata: {
            patient_name: `${customerDetails.patient_first_name} ${customerDetails.patient_last_name}`.trim(),
            hospital_number: customerDetails.patient_hospital_number || "",
          },
          onLoadStart: function() {},
          onLoadComplete: function() {},
          onComplete: function(data) {
            if (data.status === "SUCCESS") {
              axios.get(
                `https://api.uchselfservice.net/api/express/callback/monnify/?transaction_id=${invoiceRef}&monnify_ref=${data.paymentReference || monnifyRef}&amount=${totalAmount + CHARGE_FEE}&charges=${CHARGE_FEE}`
              ).then(function() {
                setPaymentStatus("success");
                clearCart();
                navigate("/payment-success", {
                  state: { paymentData: response, customerDetails, cart, chargeFee: CHARGE_FEE },
                });
              }).catch(function() {
                setPaymentStatus("failed");
              });
            } else if (data.status === "FAILED") {
              setPaymentStatus("failed");
              setScriptError("Payment failed. Please try again.");
              setIsProcessing(false);
            } else {
              setPaymentStatus("cancelled");
              setIsProcessing(false);
            }
          },
          onClose: function() {
            setPaymentStatus("cancelled");
            setIsProcessing(false);
          },
        });
      } catch {
        setScriptError("Failed to initialize Monnify payment. Please try again.");
        setPaymentStatus("failed");
      }
    },
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const status = urlParams.get("status");
    const rrr = urlParams.get("rrr");
    const orderId = urlParams.get("orderId");
    const transactionId = urlParams.get("transactionId");
    const message = urlParams.get("message");
    const reference = urlParams.get("reference");

    if (rrr && (status || message || transactionId)) {
      if (status === "01" || message?.toLowerCase() === "approved") {
        setPaymentStatus("success");
        clearCart();
        navigate("/payment-success", {
          state: {
            paymentData: { rrr, orderId, reference, status, message },
            customerDetails,
            cart,
            transactionReference: reference || checkoutResponse?.reference || "N/A",
            transactionId: transactionId,
            chargeFee: CHARGE_FEE,
          },
        });
      } else {
        setPaymentStatus("failed");
      }
    }
  }, [location, navigate, clearCart, customerDetails, cart, checkoutResponse]);

  // Handle Pay For change with auto-fill for Self
  const handlePayForChange = (e) => {
    const value = e.target.value;
    setPayFor(value);

    if (value === "self") {
      setCustomerDetails((prev) => ({
        ...prev,
        payer_first_name: prev.patient_first_name,
        payer_last_name: prev.patient_last_name,
        payer_email: prev.patient_email || user?.email || prev.payer_email,
        payer_phone_number: prev.patient_phone_number || user?.phone_number || prev.payer_phone_number,
        patient_email: user?.email || prev.patient_email,
        patient_phone_number: user?.phone_number || prev.patient_phone_number,
      }));
    }
  };

  // Handle input changes with live sync for Self mode
  const handleCustomerDetailsChange = (e) => {
    const { name, value } = e.target;

    setCustomerDetails((prev) => {
      const newDetails = { ...prev, [name]: value };

      // Live sync: when paying for self, mirror patient fields to payer fields
      if (payFor === "self") {
        if (name === "patient_first_name") newDetails.payer_first_name = value;
        if (name === "patient_last_name") newDetails.payer_last_name = value;
        if (name === "patient_email") newDetails.payer_email = value;
        if (name === "patient_phone_number") newDetails.payer_phone_number = value;
      }

      return newDetails;
    });

    // Clear error highlight for this field (and its mirrored counterpart when self)
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      if (payFor === "self") {
        if (name === "patient_first_name") next.delete("payer_first_name");
        if (name === "patient_last_name") next.delete("payer_last_name");
        if (name === "patient_email") next.delete("payer_email");
        if (name === "patient_phone_number") next.delete("payer_phone_number");
      }
      return next;
    });
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    const qty = parseInt(newQuantity) || 0;
    if (qty >= 0) {
      updateCartItem(itemId, qty);
    }
  };

  const handleRemoveItem = (itemId) => {
    removeCartItem(itemId);
  };

  const scrollToField = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
  };

  const handlePayment = async () => {
    const missing = new Set();
    if (!customerDetails.patient_first_name) missing.add("patient_first_name");
    if (!customerDetails.patient_last_name) missing.add("patient_last_name");
    if (payFor === "self" && !customerDetails.patient_email) missing.add("patient_email");
    if (payFor === "self" && !customerDetails.patient_phone_number) missing.add("patient_phone_number");
    if (!customerDetails.payer_first_name) missing.add("payer_first_name");
    if (!customerDetails.payer_last_name) missing.add("payer_last_name");
    if (!customerDetails.payer_email) missing.add("payer_email");
    if (!customerDetails.payer_phone_number) missing.add("payer_phone_number");

    if (missing.size > 0) {
      setErrorFields(missing);
      const firstId = [...missing][0];
      scrollToField(firstId);
      setScriptError("Please fill in all required fields highlighted in red.");
      return;
    }

    if (!selectedMethod) { scrollToField("payment-methods-section"); setScriptError("Please select a payment method."); return; }

    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    setShowPaymentModal(false);
    setIsProcessing(true);
    setScriptError(null);
    setPaymentStatus(null); // reset so onClose can detect next close cleanly

    try {
      const paymentData = {
        items: cart.map((item) => ({
          service: {
            id: item.id,
            name: item.name,
            description: item.description || "",
            head_code: null,
            service_type_id: null,
            price: String(parseFloat(item.price) || 0),
            amount: 0,
            service_charge: "no_free",
            fixed_price: "no",
            department: item.department || null,
            category: item.category || null,
          },
          amount: parseFloat(item.price) || 0,
          qty: parseInt(item.quantity) || 1,
        })),
        customer_meta: {
          patient_first_name: customerDetails.patient_first_name,
          patient_last_name: customerDetails.patient_last_name,
          patient_hospital_number: customerDetails.patient_hospital_number,
          patient_email: customerDetails.patient_email,
          patient_phone_number: customerDetails.patient_phone_number,
          payer_first_name: customerDetails.payer_first_name,
          payer_last_name: customerDetails.payer_last_name,
          payer_email: customerDetails.payer_email,
          payer_phone_number: customerDetails.payer_phone_number,
          transaction_description: customerDetails.transaction_description,
        },
        payment_method: selectedMethod,
        charge_fee: CHARGE_FEE,
      };

      // Remita: reuse existing RRR to avoid duplicate invoices.
      // Monnify: always get a fresh response — backend handles duplicate refs gracefully.
      const response = (selectedMethod === "remita" && checkoutResponse)
        ? checkoutResponse
        : await PaymentCheckout(paymentData);
      if (selectedMethod === "remita" && !checkoutResponse) setCheckoutResponse(response);
      setPaymentStatus("pending");

      if (paymentGateways[selectedMethod]) {
        await paymentGateways[selectedMethod](response);
      }
    } catch (error) {
      setPaymentStatus("failed");
      setScriptError("Payment initialization failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const [isRequerying, setIsRequerying] = useState(false);

  const handleRequery = async () => {
    if (!checkoutResponse) { handlePayment(); return; }
    setIsRequerying(true);
    setScriptError(null);
    try {
      const gateway = selectedMethod === "remita" ? "remita" : "monnify";
      const params = new URLSearchParams({
        transaction_id: checkoutResponse.reference || "",
        amount: String(totalAmount + CHARGE_FEE),
        charges: String(CHARGE_FEE),
      });
      if (gateway === "remita" && checkoutResponse.rrr) params.set("rrr", checkoutResponse.rrr);
      const res = await axios.get(`https://api.uchselfservice.net/api/express/callback/${gateway}/?${params.toString()}`);
      const paidStatuses = ["success", "completed", "paid"];
      if (paidStatuses.includes((res.data?.status || "").toLowerCase())) {
        setPaymentStatus("success");
        clearCart();
        navigate("/payment-success", {
          state: { paymentData: checkoutResponse, customerDetails, cart, chargeFee: CHARGE_FEE },
        });
        return;
      }
      // Still pending — reopen the gateway widget with the same invoice/RRR
      setIsProcessing(true);
      setPaymentStatus(null);
      if (paymentGateways[gateway]) {
        await paymentGateways[gateway](checkoutResponse);
      }
    } catch {
      setScriptError("Could not verify payment status. Please try again.");
    } finally {
      setIsRequerying(false);
    }
  };

  const Modal = ({ isOpen, onClose, message, onConfirm }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
              <span className="text-lg">💳</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-1">Confirm Payment</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{message}</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            {onConfirm ? (
              <>
                <Button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-700 h-10 text-sm px-5 rounded-xl">
                  Cancel
                </Button>
                <Button onClick={onConfirm} className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white h-10 text-sm px-5 rounded-xl font-semibold">
                  Proceed to Pay
                </Button>
              </>
            ) : (
              <Button onClick={onClose} className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white h-10 text-sm px-5 rounded-xl font-semibold">
                OK
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="w-full min-h-screen bg-[#f5f5f0] text-gray-900 flex flex-col">
      <SiteNavbar cartCount={cartCount} />
      <section className="flex-1 flex flex-col items-center py-8 sm:py-10">
      <div className="w-full max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-6 sm:mb-8">
          <Button
            onClick={handleBack}
            className="bg-white border border-gray-200 text-gray-700 hover:bg-green-50 hover:text-[#0d3d2e] rounded-full h-10 w-10 flex items-center justify-center transition-colors duration-200 z-10 shadow-sm"
            aria-label="Go back to previous page"
          >
            <FaArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center flex-1">
            Payment Summary
          </h1>
        </div>

        {cart.length === 0 ? (
          <p className="text-center text-base sm:text-lg md:text-xl">Your cart is empty.</p>
        ) : (
          <>
            {/* PATIENT DETAILS */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-6 sm:mb-8 shadow-sm">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 flex items-center">
                <span className="mr-2">Patient Details</span>
                <span className="ml-2 text-sm text-gray-400">
                  ({payFor === "self" ? "You" : "Someone Else"})
                </span>
              </h2>

              <div className="mb-4">
                <label className="block text-sm sm:text-base font-medium text-gray-600 mb-2 font-medium">
                  Pay For
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center text-sm sm:text-base text-gray-700">
                    <input
                      type="radio"
                      name="payFor"
                      value="self"
                      checked={payFor === "self"}
                      onChange={handlePayForChange}
                      className="mr-2"
                    />
                    Self
                  </label>
                  <label className="flex items-center text-sm sm:text-base text-gray-700">
                    <input
                      type="radio"
                      name="payFor"
                      value="someone_else"
                      checked={payFor === "someone_else"}
                      onChange={handlePayForChange}
                      className="mr-2"
                    />
                    Someone Else
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="patient_first_name"
                    type="text"
                    name="patient_first_name"
                    value={customerDetails.patient_first_name}
                    onChange={handleCustomerDetailsChange}
                    placeholder="First Name"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("patient_first_name") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="patient_last_name"
                    type="text"
                    name="patient_last_name"
                    value={customerDetails.patient_last_name}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Last Name"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("patient_last_name") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm sm:text-base mb-1 block">
                    Hospital Number <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="patient_hospital_number"
                    value={customerDetails.patient_hospital_number}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Hospital Number (if available)"
                    className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base text-gray-900"
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Patient Email {payFor === "self" && <span className="text-red-500">*</span>}
                    {payFor !== "self" && <span className="text-gray-400 text-xs ml-1">(optional)</span>}
                  </label>
                  <input
                    id="patient_email"
                    type="email"
                    name="patient_email"
                    value={customerDetails.patient_email}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Patient Email"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("patient_email") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Patient Phone Number {payFor === "self" && <span className="text-red-500">*</span>}
                    {payFor !== "self" && <span className="text-gray-400 text-xs ml-1">(optional)</span>}
                  </label>
                  <input
                    id="patient_phone_number"
                    type="tel"
                    name="patient_phone_number"
                    value={customerDetails.patient_phone_number}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Patient Phone Number"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("patient_phone_number") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
              </div>
            </div>

            {/* PAYER DETAILS */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-6 sm:mb-8 shadow-sm">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 flex items-center">
                <span className="mr-2">Payer Details</span>
                <span className="ml-2 text-sm text-gray-400">(Payment Info)</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="payer_first_name"
                    type="text"
                    name="payer_first_name"
                    value={customerDetails.payer_first_name}
                    onChange={handleCustomerDetailsChange}
                    placeholder="First Name"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("payer_first_name") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="payer_last_name"
                    type="text"
                    name="payer_last_name"
                    value={customerDetails.payer_last_name}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Last Name"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("payer_last_name") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="payer_email"
                    type="email"
                    name="payer_email"
                    value={customerDetails.payer_email}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Email"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("payer_email") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
                <div>
                  <label className="text-sm sm:text-base mb-1 block">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="payer_phone_number"
                    type="tel"
                    name="payer_phone_number"
                    value={customerDetails.payer_phone_number}
                    onChange={handleCustomerDetailsChange}
                    placeholder="Phone Number"
                    className={`w-full p-3 bg-gray-50 rounded-xl border text-sm sm:text-base text-gray-900 focus:outline-none focus:ring-2 ${errorFields.has("payer_phone_number") ? "border-red-500 ring-2 ring-red-200" : "border-gray-200 focus:ring-green-500"}`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="text-sm sm:text-base mb-1 block">
                  Transaction Description
                </label>
                <textarea
                  name="transaction_description"
                  value={customerDetails.transaction_description}
                  onChange={handleCustomerDetailsChange}
                  placeholder="Transaction Description"
                  className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base text-gray-900"
                  rows="3"
                  aria-label="Transaction Description"
                />
              </div>
            </div>

            {/* CART SUMMARY */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-6 sm:mb-8 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-gray-900">Services Selected</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">{cart.length} item{cart.length !== 1 ? "s" : ""}</span>
                  <button onClick={() => setShowServiceModal(true)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#0d3d2e] border border-green-200 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition">
                    <FaPlus className="w-2.5 h-2.5" /> Add Item
                  </button>
                </div>
              </div>
              <div className="space-y-3 mb-5">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                      <FaShoppingBag className="text-[#0d3d2e] w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">₦{(parseFloat(item.price)||0).toLocaleString()} × {parseInt(item.quantity)||1}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2 py-1">
                        <button onClick={() => handleQuantityChange(item.id, (parseInt(item.quantity)||1) - 1)}
                          className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-[#0d3d2e] font-bold text-base leading-none">−</button>
                        <span className="text-sm font-bold text-gray-900 min-w-[20px] text-center">{item.quantity}</span>
                        <button onClick={() => handleQuantityChange(item.id, (parseInt(item.quantity)||1) + 1)}
                          className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-[#0d3d2e] font-bold text-base leading-none">+</button>
                      </div>
                      <p className="text-sm font-bold text-gray-900 min-w-[70px] text-right">₦{((parseFloat(item.price)||0) * (parseInt(item.quantity)||1)).toLocaleString()}</p>
                      <button onClick={() => handleRemoveItem(item.id)}
                        className="text-red-400 hover:text-red-600 transition p-1" aria-label={`Remove ${item.name}`}>
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal ({cart.length} item{cart.length !== 1 ? "s" : ""})</span>
                  <span className="font-semibold text-gray-900">₦{totalAmount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Service Charge</span>
                  <span className="font-semibold text-gray-900">₦{CHARGE_FEE.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className="text-base font-bold text-gray-900">Total</span>
                  <span className="text-xl font-black text-[#0d3d2e]">₦{(totalAmount + CHARGE_FEE).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* PAYMENT METHODS */}
            <div id="payment-methods-section" className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-6 sm:mb-8 shadow-sm">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4">Select Payment Method</h2>
              {isLoadingMethods ? (
                <p className="text-center text-sm sm:text-base">Loading payment methods...</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.key}
                      className={`flex flex-col items-center p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedMethod === method.key
                          ? "border-[#0d3d2e] bg-green-50"
                          : "border-gray-200 hover:bg-green-50 hover:border-green-300"
                      }`}
                      onClick={() => setSelectedMethod(method.key)}
                    >
                      <img
                        src={paymentImages[method.key] || "https://via.placeholder.com/100x40?text=Payment"}
                        alt={method.name}
                        className="h-8 sm:h-10 mb-2 object-contain w-full"
                      />
                      <span className="text-xs sm:text-sm font-medium text-center">{method.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {scriptError && (
              <div className="text-center mb-4 sm:mb-6">
                <p className="text-red-500 text-sm sm:text-base">{scriptError}</p>
              </div>
            )}

            {/* PAYMENT STATUS & BUTTONS */}
            {paymentStatus === "success" ? (
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4 text-green-500">
                  Payment Initiated!
                </h3>
                <p className="mb-4 text-sm sm:text-base">
                  Your payment is being processed. Please complete the payment in the gateway.
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => navigate("/")}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6"
                  >
                    Back to Home
                  </Button>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">How was your experience?</p>
                  <button
                    onClick={() => navigate("/feedback")}
                    className="inline-flex items-center gap-2 bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-green-700 transition"
                  >
                    💬 Give Feedback
                  </button>
                </div>
              </div>
            ) : (paymentStatus === "failed" || paymentStatus === "cancelled") ? (
              <div className="text-center">
                <h3 className={`text-xl sm:text-2xl font-semibold mb-3 ${paymentStatus === "cancelled" ? "text-yellow-600" : "text-red-500"}`}>
                  {paymentStatus === "cancelled" ? "Payment Cancelled" : "Payment Failed"}
                </h3>
                <p className="mb-4 text-sm text-gray-600">
                  {paymentStatus === "cancelled"
                    ? "You closed the payment window. Click below to try again — your order is still saved."
                    : "Something went wrong. Please try again or select another method."}
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <Button onClick={handleBack} className="bg-gray-100 hover:bg-gray-200 text-gray-700 h-10 px-5 rounded-xl text-sm">
                    Back
                  </Button>
                  {checkoutResponse && (
                    <Button
                      onClick={handleRequery}
                      disabled={isRequerying || isProcessing}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-5 rounded-xl text-sm disabled:opacity-60"
                    >
                      {isRequerying ? "Checking..." : "Requery"}
                    </Button>
                  )}
                  <Button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white h-10 px-5 rounded-xl text-sm disabled:opacity-60"
                  >
                    {isProcessing ? "Processing..." : "Try Again"}
                  </Button>
                </div>
              </div>
            ) : paymentStatus === "pending" ? (
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4 text-yellow-500">
                  Payment Processing, Please Wait...
                </h3>
                <p className="mb-4 text-sm sm:text-base">
                  Your payment is being processed. Please wait...
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handlePayment}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6"
                  >
                    Retry Payment
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-11 text-sm sm:text-base px-4 sm:px-6 disabled:opacity-50"
                  >
                    {isProcessing ? "Processing..." : "Proceed to Payment"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        message="Please ensure the email is valid for future reference."
      />
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={confirmPayment}
        message={`A charge fee of ₦${CHARGE_FEE.toFixed(2)} will be applied in addition to the subtotal of ₦${totalAmount.toFixed(2)}. Total amount to be charged: ₦${(totalAmount + CHARGE_FEE).toFixed(2)} without remitta charge. Proceed with payment?`}
      />
      <ServiceSelectorModal
        open={showServiceModal}
        onClose={() => setShowServiceModal(false)}
        redirectTo="/receipt"
      />
      </section>
    </div>
  );
}